import { z } from "zod";

const quantitySchema = z
  .union([z.string(), z.number()])
  .transform(String)
  .pipe(z.string().regex(/^\d{1,11}(\.\d{1,3})?$/))
  .refine((value) => Number(value) > 0, "Quantity must be positive");
const moneySchema = z
  .union([z.string(), z.number()])
  .transform(String)
  .pipe(z.string().regex(/^\d{1,12}(\.\d{1,2})?$/));
const idempotencyKeySchema = z.string().trim().min(8).max(160);

export const inventoryLocationCreateSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(1)
      .max(40)
      .transform((value) => value.toUpperCase()),
    name: z.string().trim().min(1).max(120),
    isDefault: z.boolean().default(false),
  })
  .strict();
export type InventoryLocationCreateDto = z.infer<
  typeof inventoryLocationCreateSchema
>;

export const inventoryLocationUpdateSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(1)
      .max(40)
      .transform((value) => value.toUpperCase())
      .optional(),
    name: z.string().trim().min(1).max(120).optional(),
    active: z.boolean().optional(),
    isDefault: z.boolean().optional(),
  })
  .strict();
export type InventoryLocationUpdateDto = z.infer<
  typeof inventoryLocationUpdateSchema
>;

export const inventoryReceiptSchema = z
  .object({
    catalogItemId: z.string().uuid(),
    locationId: z.string().uuid(),
    quantity: quantitySchema,
    unitCost: moneySchema.optional(),
    note: z.string().trim().max(1000).optional(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();
export type InventoryReceiptDto = z.infer<typeof inventoryReceiptSchema>;

export const inventoryAdjustmentSchema = z
  .object({
    catalogItemId: z.string().uuid(),
    locationId: z.string().uuid(),
    direction: z.enum(["in", "out"]),
    quantity: quantitySchema,
    unitCost: moneySchema.optional(),
    reason: z.string().trim().min(1).max(1000),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();
export type InventoryAdjustmentDto = z.infer<typeof inventoryAdjustmentSchema>;

export const inventoryTransferSchema = z
  .object({
    catalogItemId: z.string().uuid(),
    fromLocationId: z.string().uuid(),
    toLocationId: z.string().uuid(),
    quantity: quantitySchema,
    note: z.string().trim().max(1000).optional(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict()
  .refine((value) => value.fromLocationId !== value.toLocationId, {
    path: ["toLocationId"],
    message: "Source and destination locations must differ",
  });
export type InventoryTransferDto = z.infer<typeof inventoryTransferSchema>;

export const inventoryIssueSchema = z
  .object({
    catalogItemId: z.string().uuid(),
    locationId: z.string().uuid(),
    projectId: z.string().uuid(),
    quantity: quantitySchema,
    note: z.string().trim().max(1000).optional(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();
export type InventoryIssueDto = z.infer<typeof inventoryIssueSchema>;

export const inventoryReturnSchema = z
  .object({
    catalogItemId: z.string().uuid(),
    locationId: z.string().uuid(),
    projectId: z.string().uuid(),
    sourceMovementId: z.string().uuid().optional(),
    quantity: quantitySchema,
    note: z.string().trim().max(1000).optional(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();
export type InventoryReturnDto = z.infer<typeof inventoryReturnSchema>;
